import {
  ExtractedData,
  extractedDataToObjects,
  ExtractedObject,
  DataPoint,
  NewOrganizationInvoice,
  Customer,
} from '../../types'
import {
  PayloadAction,
  payloadActionCreator,
  Loadable,
  FailableAction,
  failableActionCreator,
  actionCreator,
  Action,
} from '@library/react-toolkit'
import {
  either,
  record,
  array,
  option,
  function as func,
  tuple,
  semigroup,
  monoid,
} from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import * as t from 'io-ts'

export const AssignableKeys = t.keyof({
  amount: null,
  reference: null,
  createdAt: null,
  dueAt: null,

  payerName: null,
  payerEmail: null,
  payerPhonenumber: null,
  payerAddress: null,
  payerTaxNumber: null,

  discountName: null,
  discountRate: null,

  taxName: null,
  taxRate: null,

  organization: null,
  email: null,
})
export type AssignableKeys = t.TypeOf<typeof AssignableKeys>

export const requiredKeys = (
  mode: 'organization' | 'supplier'
): AssignableKeys[] =>
  mode === 'organization'
    ? [
        'payerName',
        'payerEmail',
        'payerPhonenumber',
        'amount',
        'reference',
        'createdAt',
        'dueAt',
      ]
    : ['organization', 'email', 'amount', 'reference', 'createdAt', 'dueAt']

type SelectState = {
  data: Record<string, ExtractedData>
  selectedSheets: string[]
}

type AssignState = SelectState & {
  assignments: Record<string, Record<AssignableKeys, string>>
  memo: Customer[]
}

export type BillOptional = {
  [k in AssignableKeys]: k extends 'taxRate' | 'discountRate'
    ? option.Option<number>
    : option.Option<IndexBill<k>>
}

export const emptyBillOptional: BillOptional = {
  amount: option.none,
  reference: option.none,
  createdAt: option.none,
  dueAt: option.none,
  payerTaxNumber: option.none,
  payerEmail: option.none,
  payerAddress: option.none,
  payerPhonenumber: option.none,
  payerName: option.none,

  discountName: option.none,
  discountRate: option.none,

  taxName: option.none,
  taxRate: option.none,

  organization: option.none,
  email: option.none,
}

type ConfirmState = AssignState & {
  finalData: BillOptional[]
  uploadResult: Loadable<unknown>
}

export type State =
  | { state: 'Closed' }
  | { state: 'Open' }
  | { state: 'Loading' }
  | { state: 'Error'; error: Error }
  | ({ state: 'Select' } & SelectState)
  | ({ state: 'Assign' } & AssignState)
  | ({ state: 'Confirm' } & ConfirmState)

export type StartWizardAction = Action<'UPLOAD/start_wizard'>
export const startWizardAction = actionCreator<StartWizardAction>(
  'UPLOAD/start_wizard'
)

export type SelectFileAction = PayloadAction<'UPLOAD/select_file', File>
export const selectFileAction =
  payloadActionCreator<SelectFileAction>('UPLOAD/select_file')

export type ParseFileResult = PayloadAction<
  'UPLOAD/parse_file_result',
  either.Either<Error, Record<string, ExtractedData>>
>
export const parseFileResult = payloadActionCreator<ParseFileResult>(
  'UPLOAD/parse_file_result'
)

export type CancelUploadAction = Action<'UPLOAD/cancel'>
export const cancelUploadAction =
  actionCreator<CancelUploadAction>('UPLOAD/cancel')

export type SelectSheetAction = PayloadAction<'UPLOAD/select_sheet', string>
export const selectSheetAction = payloadActionCreator<SelectSheetAction>(
  'UPLOAD/select_sheet'
)

export type SetAssignmentAction = PayloadAction<
  'UPLOAD/set_assignment',
  [string, AssignableKeys, string]
>
export const setAssignmentAction = payloadActionCreator<SetAssignmentAction>(
  'UPLOAD/set_assignment'
)

export type StepForwardAction = PayloadAction<
  'UPLOAD/step_forward',
  'organization' | 'supplier'
>
export const stepForwardAction = payloadActionCreator<StepForwardAction>(
  'UPLOAD/step_forward'
)

export type StepBackAction = Action<'UPLOAD/step_back'>
export const stepBackAction = actionCreator<StepBackAction>('UPLOAD/step_back')

export type SendResult = FailableAction<'UPLOAD/send_result', unknown>
export const sendResult =
  failableActionCreator<SendResult>('UPLOAD/send_result')

export type SetPayerMemo = PayloadAction<'UPLOAD/set_payer_memo', Customer[]>
export const setPayerMemo = payloadActionCreator<SetPayerMemo>(
  'UPLOAD/set_payer_memo'
)

export type ConfirmUploadAction = PayloadAction<
  'UPLOAD/confirm_upload',
  'organization' | 'supplier'
>
export const confirmUploadAction = payloadActionCreator<ConfirmUploadAction>(
  'UPLOAD/confirm_upload'
)

export type Actions =
  | StartWizardAction
  | SelectFileAction
  | ParseFileResult
  | CancelUploadAction
  | SelectSheetAction
  | SetAssignmentAction
  | SetPayerMemo
  | StepBackAction
  | StepForwardAction
  | ConfirmUploadAction
  | SendResult

export const monoidBill = monoid.struct<BillOptional>({
  amount: option.getMonoid(semigroup.first<number>()),
  reference: option.getMonoid(semigroup.first<string>()),
  createdAt: option.getMonoid(semigroup.first<Date>()),
  dueAt: option.getMonoid(semigroup.first<Date>()),

  payerEmail: option.getMonoid(semigroup.first<string>()),
  payerAddress: option.getMonoid(semigroup.first<string>()),
  payerTaxNumber: option.getMonoid(semigroup.first<string>()),
  payerPhonenumber: option.getMonoid(semigroup.first<string>()),
  payerName: option.getMonoid(semigroup.first<string>()),

  discountName: option.getMonoid(semigroup.first<string>()),
  discountRate: option.getMonoid(semigroup.first<number>()),

  taxName: option.getMonoid(semigroup.first<string>()),
  taxRate: option.getMonoid(semigroup.first<number>()),

  organization: option.getMonoid(semigroup.first<string>()),
  email: option.getMonoid(semigroup.first<string>()),
})

const dataPointToString = (d: DataPoint) =>
  option.some(typeof d === 'string' ? d : d.toString())

const dataPointToNumber = (d: DataPoint) =>
  pipe(
    d,
    option.fromPredicate((x): x is number => typeof x === 'number'),
    option.alt(() =>
      pipe(
        d,
        dataPointToString,
        option.map(parseFloat),
        option.filter(n => !isNaN(n))
      )
    )
  )

const dataPointToDate = (d: DataPoint) =>
  d instanceof Date
    ? option.some(d)
    : pipe(
        d,
        dataPointToString,
        option.map(ds => new Date(ds)),
        option.filter(n => !isNaN(n.getTime()))
      )

export type IndexBill<K extends AssignableKeys> = K extends
  | 'taxRate'
  | 'discountRate'
  ? number
  : K extends keyof NewOrganizationInvoice
  ? NewOrganizationInvoice[K]
  : string

const extractDataPoint =
  (assignments: Record<AssignableKeys, string>, o: ExtractedObject) =>
  <K extends AssignableKeys>(
    k: K,
    conversion: (d: DataPoint) => option.Option<IndexBill<K>>
  ): option.Option<IndexBill<K>> =>
    pipe(record.lookup(assignments[k], o), option.chain(conversion))

const billFromAssignments =
  (assignments: Record<AssignableKeys, string>) =>
  (o: ExtractedObject): BillOptional => {
    const extract = extractDataPoint(assignments, o)

    return {
      amount: extract('amount', dataPointToNumber),
      reference: extract('reference', dataPointToString),
      createdAt: extract('createdAt', dataPointToDate),
      dueAt: extract('dueAt', dataPointToDate),

      payerEmail: extract('payerEmail', dataPointToString),
      payerAddress: extract('payerAddress', dataPointToString),
      payerTaxNumber: extract('payerTaxNumber', dataPointToString),
      payerPhonenumber: extract('payerPhonenumber', dataPointToString),
      payerName: extract('payerName', dataPointToString),

      discountName: extract('discountName', dataPointToString),
      discountRate: extract('discountRate', dataPointToNumber),

      taxName: extract('taxName', dataPointToString),
      taxRate: extract('taxRate', dataPointToNumber),

      organization: extract('organization', dataPointToString),
      email: extract('email', dataPointToString),
    }
  }

export const finalDataFromConfirmState = (
  data: Record<string, ExtractedData>,
  assignments: Record<string, Record<AssignableKeys, string>>
): BillOptional[] =>
  pipe(
    assignments,
    record.mapWithIndex((name, as) => func.tuple(as, data[name])),
    record.map(tuple.mapSnd(extractedDataToObjects)),
    record.map(([as, ds]) => pipe(ds, array.map(billFromAssignments(as)))),
    record.collect((_, v) => v),
    array.flatten
  )

export const withDefaults = (b: BillOptional) => ({
  amount: option.getOrElse(() => 0)(b.amount),
  reference: option.getOrElse(() => '')(b.reference),
  createdAt: option.getOrElse(() => new Date())(b.createdAt),
  dueAt: option.getOrElse(() => new Date())(b.dueAt),
  payerName: option.getOrElse(() => '')(b.payerName),
  payerEmail: option.getOrElse(() => '')(b.payerEmail),
  payerAddress: option.getOrElse(() => '')(b.payerAddress),
  payerTaxNumber: option.getOrElse(() => '')(b.payerTaxNumber),
  payerPhonenumber: option.getOrElse(() => '')(b.payerPhonenumber),

  discountName: option.getOrElse(() => '')(b.discountName),
  discountRate: option.toUndefined(b.discountRate),

  taxName: option.getOrElse(() => '')(b.taxName),
  taxRate: option.toUndefined(b.taxRate),

  organization: option.getOrElse(() => '')(b.organization),
  email: option.getOrElse(() => '')(b.email),
})

export const getUploadData = ({ finalData }: ConfirmState) =>
  pipe(finalData, array.map(withDefaults))
